import React, { FunctionComponent, useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { filter, append, equals, any, isNil, find } from 'ramda';
import { FormattedMessage } from 'react-intl';
import { IAppState } from '../../../../common/models/store/IAppState';
import { ISurveyQuestion } from '../../../../common/models/survey/ISurveyQuestion';
import {
  setSelectedQuestionsRoutine,
  createQuestionRoutine,
  updateQuestionRoutine,
  deleteQuestionRoutine
} from '../../routines';
import { IBindingCallback1, IBindingAction } from '../../../../common/models/callback';
import SearchInput from '../../../../components/SearchInput';
import { IQuestionWithOwner } from '../../../../common/models/question/IQuestionWithOwner';
import Table from '../../../../components/Table';
import { getColumns, orderQuestions } from './utils';
import { searchByWords } from '../../../../common/helpers/searchHelper';
import { getUpdatedData, getDefaultSelected, getSelected } from '../SurveyBuilder/utils';
import { User } from '../../../../common/models/user/User';
import { IQuestionText } from '../../../../common/models/question/IQuestionText';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import RegularButton from '../../../../components/RegularButton';
import Icon from '../../../../components/Icon';
import Input from '../../../../components/Input';
import { errorToast } from '../../../../common/helpers/toastrHelper';
import NoDataPlaceholder from '../../components/NoDataPlaceholder';
import { IFilter } from '../../../../common/models/filter/IFilter';
import { ISortItem } from '../../../../common/models/filter/ISortItem';
import { QuestionSection } from '../../../../common/enums/QuestionSection';

interface IProps {
  selectedQuestions: ISurveyQuestion[];
  questions: IQuestionWithOwner[];
  userId: string;
  onClose: IBindingAction;
  createQuestion: IBindingCallback1<string>;
  updateQuestion: IBindingCallback1<IQuestionText>;
  deleteQuestion: IBindingCallback1<string>;
  setSelectedQuestions: IBindingCallback1<ISurveyQuestion[]>;
}

const QuestionsPage: FunctionComponent<IProps> = ({
  onClose,
  selectedQuestions,
  questions,
  userId,
  setSelectedQuestions,
  createQuestion,
  updateQuestion,
  deleteQuestion
}) => {
  const defaultSelectedIds = getDefaultSelected(false, questions, selectedQuestions);
  const [isDesc, setIsDesc] = useState(true);
  const [deletingId, setDeletingId] = useState<null | string>(null);
  const [search, setSearch] = useState('');
  const [data, setData] = useState(orderQuestions(isDesc, questions, userId));
  const [selectedIds, setSelectedIds] = useState(defaultSelectedIds);
  const [questionText, setQuestionText] = useState<null | string>(null);
  const shownSelected = getSelected(data, selectedIds);
  const allSelected = getSelected(questions, selectedIds);

  const updateData = () => {
    let updatedData = [...questions];
    if (search.trim()) {
      updatedData = filter(({ text }) => searchByWords(search, text), updatedData);
    }
    setData(orderQuestions(isDesc, updatedData, userId));
  };

  const onEdit = (questionData: IQuestionText) => {
    if (questionData.text.trim()) {
      updateQuestion(questionData);
    } else {
      errorToast('Cannot save empty question', 'freeResponse.editError');
    }
  };

  useEffect(updateData, [isDesc]);

  const actions = { onDelete: setDeletingId, onEdit };
  const [columns, setColumns] = useState(getColumns(search, userId, actions));
  const isFirstRun = useRef(true);

  useEffect(() => {
    updateData();
    setColumns(getColumns(search, userId, actions));
  }, [search.trim()]);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }

    if (deletingId) {
      setSelectedIds(state => filter(id => id !== deletingId, state));
      setDeletingId(null);
      return;
    }

    if (search.trim()) {
      setSearch('');
    }

    setSelectedIds(append(questions[questions.length - 1].id, selectedIds));
  }, [questions.length]);

  useEffect(() => {
    updateData();
  }, [questions]);

  const onRowSelected = (row: IQuestionWithOwner, isSelected: boolean) => {
    setSelectedIds(state => (isSelected
      ? append(row.id, state)
      : filter(id => id !== row.id, state)));
  };

  const onApply = () => {
    setSelectedQuestions(getUpdatedData(false, allSelected, selectedQuestions));
    onClose();
  };

  const onAddQuestion = () => {
    createQuestion(questionText?.trim() as string);
    setQuestionText(null);
  };

  const onAddCancel = () => setQuestionText(null);

  const isAddBttnDisabled = (
    !questionText?.trim()
    || any(({ text }) => text.toLowerCase() === questionText.toLowerCase().trim(), questions)
  );

  const updateSortBy = ({ sort }: Partial<IFilter>) => {
    const option = find(({ id }) => id === 'author', sort as ISortItem[]);
    setIsDesc(option?.desc as boolean);
  };

  return (
    <div className="sb-question-page">
      <ConfirmationModal
        isShown={Boolean(deletingId)}
        titleText={{
          id: 'freeResponse.delete',
          defaultMessage: 'Delete question'
        }}
        descriptionText={{
          id: 'freeResponse.confirmDelete',
          defaultMessage: 'Are you sure you want to delete this question?'
        }}
        onConfirm={() => deleteQuestion(deletingId as string)}
        onCancel={() => setDeletingId(null)}
        confirmBttnText={{ id: 'delete', defaultMessage: 'Delete' }}
      />
      <header className="sb-header">
        <FormattedMessage id="questions.bankTitle" defaultMessage="Question bank" />
      </header>
      <main className="sb-question-main">
        <div className="sb-table-title">
          <FormattedMessage
            id="questions.bankDescription"
            defaultMessage="Select free response and yes/no questions to add or remove"
            values={{ value: allSelected.length }}
          />
          <SearchInput
            value={search}
            onChange={value => setSearch(value)}
            defaultPlaceholder="Search questions"
            intlId="surveyBuilder.searchPlaceholder"
          />
        </div>
        <div>
          <Table
            config={{
              columns,
              data: { items: data, total: data.length },
              loading: false,
              autoHeight: true,
              rowHeight: 65,
              noDataPlaceholderHeight: 400,
              rowSelection: 'multiple',
              selectionType: 'combine',
              hideCheckboxHeader: true,
              onRowSelected,
              defaultSelected: shownSelected,
              noDataPlaceholder: <NoDataPlaceholder variant="questionBank" />,
              sortingEnabled: true,
              defaultSorted: [{ desc: isDesc, id: 'author' }],
              onFilterUpdated: updateSortBy
            }}
          />
        </div>
      </main>
      <footer className="sb-footer">
        {isNil(questionText)
          ? (
            <>
              <RegularButton
                variant="secondary"
                defaultMessage="Create new question"
                textIntlId="freeResponse.create"
                icon={<Icon icon="plus" />}
                onClick={() => setQuestionText('')}
              />
              <div className="sb-footer-bttns">
                <RegularButton
                  variant="tertiary"
                  onClick={onClose}
                  textIntlId="cancel"
                  defaultMessage="Cancel"
                />
                <RegularButton
                  textIntlId="surveyBuilder.apply"
                  defaultMessage="Apply selection"
                  onClick={onApply}
                  disabled={equals(defaultSelectedIds, selectedIds)}
                />
              </div>
            </>
          )
          : (
            <>
              <Input autoFocus className="t-14 mr-5" onChange={setQuestionText} />
              <div className="sb-footer-bttns">
                <RegularButton
                  textIntlId="cancel"
                  defaultMessage="Cancel"
                  onClick={onAddCancel}
                  variant="tertiary"
                />
                <RegularButton
                  textIntlId="surveyBuilder.add"
                  defaultMessage="Add and select"
                  onClick={onAddQuestion}
                  disabled={isAddBttnDisabled}
                />
              </div>
            </>
          )}
      </footer>
    </div>
  );
};

const mapStateToProps = ({
  surveyBuilder: { selectedQuestions, questions },
  user: { user }
}: IAppState) => ({
  selectedQuestions,
  questions: questions[QuestionSection.Questions],
  userId: (user as User).id
});

const mapDispatchToProps = {
  setSelectedQuestions: setSelectedQuestionsRoutine,
  createQuestion: createQuestionRoutine,
  updateQuestion: updateQuestionRoutine,
  deleteQuestion: deleteQuestionRoutine
};

export default connect(mapStateToProps, mapDispatchToProps)(QuestionsPage);
